import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Users, Handshake, CheckSquare, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = {
  contact: { label: "Contact", icon: Users, title: "Add contact" },
  opportunity: { label: "Opportunity", icon: Handshake, title: "Add opportunity" },
  task: { label: "Task", icon: CheckSquare, title: "Add task" },
  event: { label: "Calendar event", icon: CalendarDays, title: "Add calendar event" },
};

const DEFAULTS = {
  contact: { name: "", email: "", company: "", type: "lead" },
  opportunity: { title: "", value: "", contact_name: "", stage: "", expected_close: "" },
  task: { title: "", priority: "medium", due_date: "" },
  event: { title: "", date: "", time: "", notes: "" },
};

export default function QuickAdd() {
  const queryClient = useQueryClient();
  const [type, setType] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: stages = [] } = useQuery({
    queryKey: ["pipeline-stages"],
    queryFn: async () => (await api.get("/pipeline/stages")).data.stages,
    enabled: type === "opportunity",
  });

  const openType = (t) => { setForm({ ...DEFAULTS[t] }); setType(t); };
  const close = () => { setType(null); setSaving(false); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (type === "opportunity" && !form.stage && stages.length) {
      setForm((f) => ({ ...f, stage: stages[0].id }));
    }
  }, [type, stages, form.stage]);

  const required = { contact: "name", opportunity: "title", task: "title", event: "date" };
  const canSave = () => {
    if (type === "contact") return !!form.name?.trim();
    if (type === "opportunity") return !!form.title?.trim();
    if (type === "task") return !!form.title?.trim();
    if (type === "event") return !!form.title?.trim() && !!form.date;
    return false;
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (type === "contact") {
        await api.post("/contacts", { name: form.name, email: form.email, company: form.company, type: form.type });
      } else if (type === "opportunity") {
        await api.post("/deals", {
          title: form.title,
          value: parseFloat(form.value) || 0,
          contact_name: form.contact_name,
          stage: form.stage || stages[0]?.id || "new",
          expected_close: form.expected_close || null,
        });
      } else if (type === "task") {
        await api.post("/tasks", { title: form.title, priority: form.priority, due_date: form.due_date || null, status: "todo" });
      } else if (type === "event") {
        await api.post("/events", { title: form.title, date: form.date, time: form.time, notes: form.notes });
      }
      toast.success(`${TYPES[type].label} added`);
      queryClient.invalidateQueries();
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid="quick-add-button"><Plus className="mr-2 h-4 w-4" /> Quick add</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {Object.entries(TYPES).map(([key, t]) => (
            <DropdownMenuItem key={key} onClick={() => openType(key)} data-testid={`quick-add-${key}`}>
              <t.icon className="mr-2 h-4 w-4 text-muted-foreground" /> {t.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!type} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle>{type ? TYPES[type].title : ""}</DialogTitle>
            <DialogDescription>{type ? `Quickly add a new ${TYPES[type].label.toLowerCase()} without leaving the dashboard.` : ""}</DialogDescription>
          </DialogHeader>

          {type === "contact" && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={set("name")} placeholder="Jane Client" data-testid="qa-contact-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="jane@co.com" data-testid="qa-contact-email" /></div>
                <div className="space-y-1.5"><Label>Company</Label><Input value={form.company} onChange={set("company")} data-testid="qa-contact-company" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={set("type")}>
                  <SelectTrigger data-testid="qa-contact-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="lead">Prospect</SelectItem><SelectItem value="client">Client</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}

          {type === "opportunity" && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={set("title")} placeholder="Acme — Q3 retainer" data-testid="qa-opp-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Value ($)</Label><Input type="number" value={form.value} onChange={set("value")} placeholder="5000" data-testid="qa-opp-value" /></div>
                <div className="space-y-1.5"><Label>Client / contact</Label><Input value={form.contact_name} onChange={set("contact_name")} data-testid="qa-opp-contact" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Stage</Label>
                  <Select value={form.stage || ""} onValueChange={set("stage")}>
                    <SelectTrigger data-testid="qa-opp-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
                    <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Expected close</Label><Input type="date" value={form.expected_close} onChange={set("expected_close")} data-testid="qa-opp-close" /></div>
              </div>
            </div>
          )}

          {type === "task" && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Task</Label><Input value={form.title} onChange={set("title")} placeholder="Follow up with Acme" data-testid="qa-task-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={set("priority")}>
                    <SelectTrigger data-testid="qa-task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="qa-task-due" /></div>
              </div>
            </div>
          )}

          {type === "event" && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={set("title")} placeholder="Discovery call" data-testid="qa-event-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="qa-event-date" /></div>
                <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time} onChange={set("time")} data-testid="qa-event-time" /></div>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="qa-event-notes" /></div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={saving} data-testid="quick-add-cancel">Cancel</Button>
            <Button onClick={submit} disabled={!canSave() || saving} data-testid="quick-add-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
